/**
 * Pure view-model helpers for calls (1:1 AND group).
 *
 * Group calls are additive over 1:1 (backend MatrixSentry #43071): the same
 * endpoints and `POST /calls/start {callee_ids:[...]}`. Checked HTTP supplies
 * `is_group_call` and the roster; private v1 hints carry only IDs. **In a group call
 * `callee_id` is a sentinel and `participants[]` is the source of truth** — so
 * roster/title derivation must branch on `is_group_call`, never assume the 1:1
 * `callee_*` fields.
 *
 * Keeping this logic pure lets the incoming-call modal, the in-call room, and the
 * history list all share one tested source of truth.
 */
export interface CallParticipant {
  userId: string;
  name?: string;
  avatar?: string;
  /** backend participant status, e.g. 'connecting' | 'connected' | 'disconnected' */
  status?: string;
}

/**
 * Raw participant as the backend sends it (MatrixSentry #44500):
 * `{id, user_id, user_name, role, status, video, audio}` — note `user_name`
 * (NOT `name`) and NO avatar (resolve from the directory by `user_id`). We read
 * tolerantly so both this shape and our normalized one work.
 */
export interface RawCallParticipant {
  id?: string;
  user_id?: string;
  userId?: string;
  user_name?: string;
  name?: string;
  avatar?: string;
  role?: string;
  status?: string;
  [k: string]: unknown;
}

export interface Call {
  id: string;
  caller_id?: string;
  callee_id?: string;
  caller_name?: string;
  callee_name?: string;
  caller_avatar?: string;
  callee_avatar?: string;
  type?: 'voice' | 'video' | string;
  /** Legacy read alias; native v1 call hints contain no media type. */
  call_type?: 'voice' | 'video' | string;
  status?: 'ringing' | 'accepted' | 'rejected' | 'ended' | 'timeout' | string;
  is_group_call?: boolean;
  participants?: RawCallParticipant[];
  started_at?: string;
  ended_at?: string;
  duration_sec?: number;
  recording_url?: string;
  [k: string]: unknown;
}

/** Normalize a raw backend participant; prefers `user_id`/`user_name`. */
function normalizeParticipant(p: RawCallParticipant): CallParticipant | null {
  const userId = p.user_id ?? p.userId ?? p.id;
  if (!userId) return null;
  return { userId, name: p.user_name ?? p.name, avatar: p.avatar, status: p.status };
}

/** Media type from an HTTP view, tolerating the legacy read alias. */
export function callType(call: Call): string | undefined {
  return call.type ?? call.call_type;
}

export function isGroupCall(call: Call): boolean {
  if (call.is_group_call === true) return true;
  // Fallback for payloads that omit the flag: >2 distinct participants.
  return (call.participants?.length ?? 0) > 2;
}

function dedupe(roster: CallParticipant[]): CallParticipant[] {
  const seen = new Set<string>();
  const out: CallParticipant[] = [];
  for (const p of roster) {
    if (!p.userId || seen.has(p.userId)) continue;
    seen.add(p.userId);
    out.push(p);
  }
  return out;
}

/**
 * The participants of a call. Group → `participants[]`; 1:1 → caller + callee
 * synthesized from the flat fields. Always deduped by userId; entries without a
 * userId are dropped.
 */
export function callRoster(call: Call): CallParticipant[] {
  if (isGroupCall(call) && call.participants?.length) {
    const normalized = call.participants
      .map(normalizeParticipant)
      .filter((p): p is CallParticipant => p !== null);
    return dedupe(normalized);
  }
  const synthesized: CallParticipant[] = [];
  if (call.caller_id) {
    synthesized.push({ userId: call.caller_id, name: call.caller_name, avatar: call.caller_avatar });
  }
  if (call.callee_id) {
    synthesized.push({ userId: call.callee_id, name: call.callee_name, avatar: call.callee_avatar });
  }
  // A group call with no participants[] yet still falls through to here; its
  // sentinel callee_id would pollute the roster, so drop it.
  const roster = isGroupCall(call)
    ? synthesized.filter((p) => p.userId === call.caller_id)
    : synthesized;
  return dedupe(roster);
}

/** Display title from the current user's perspective. */
export function callDisplayTitle(call: Call, currentUserId?: string, copy = { call: 'Llamada', group: (count: number) => `Llamada grupal · ${count}` }): string {
  if (isGroupCall(call)) {
    return copy.group(callRoster(call).length);
  }
  // 1:1 → show the OTHER party.
  if (currentUserId && call.caller_id === currentUserId) {
    return call.callee_name?.trim() || copy.call;
  }
  if (currentUserId && call.callee_id === currentUserId) {
    return call.caller_name?.trim() || copy.call;
  }
  // Perspective unknown — prefer callee, then caller.
  return call.callee_name?.trim() || call.caller_name?.trim() || copy.call;
}
